import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, MapPin, Eye, EyeOff, Loader2, CheckCircle, Tag, Users, Zap, ArrowRight, Building2, FileText, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, useLanguage, API, HEADER_LOGO_DARK } from '../App';
import axios from '../lib/axios';
import { SafariDropdown } from '../components/ui/safari-dropdown';

// Translations for Start Selling page
const SELLING_TRANSLATIONS = {
  en: {
    hero_title: "Ready to sell something?",
    hero_subtitle: "Join thousands of sellers on the Caribbean's fastest growing marketplace",
    benefit_1: "Free listings",
    benefit_1_desc: "List your items without any fees",
    benefit_2: "Reach local buyers",
    benefit_2_desc: "Connect with buyers in your area",
    benefit_3: "Quick & Easy",
    benefit_3_desc: "Start selling in under 2 minutes",
    create_account: "Create your seller account",
    already_have_account: "Already have an account?",
    sign_in: "Sign in",
    full_name: "Full Name",
    email: "Email Address",
    phone: "Phone Number",
    location: "Location",
    password: "Password",
    confirm_password: "Confirm Password",
    start_selling: "Start Selling",
    creating_account: "Creating account...",
    or_continue_with: "Or continue with",
    private_account: "Private",
    business_account: "Business",
    business_name: "Business Name",
    kvk_number: "Chamber of Commerce (KVK)",
    kvk_document: "KVK Document",
    id_document: "Owner ID Document",
    upload_document: "Upload Document",
    document_uploaded: "Document uploaded",
  },
  nl: {
    hero_title: "Iets verkopen?",
    hero_subtitle: "Sluit je aan bij duizenden verkopers op de snelst groeiende marktplaats van het Caribisch gebied",
    benefit_1: "Gratis advertenties",
    benefit_1_desc: "Plaats je items zonder kosten",
    benefit_2: "Bereik lokale kopers",
    benefit_2_desc: "Maak contact met kopers bij jou in de buurt",
    benefit_3: "Snel & Eenvoudig",
    benefit_3_desc: "Begin met verkopen in minder dan 2 minuten",
    create_account: "Maak je verkopersaccount aan",
    already_have_account: "Heb je al een account?",
    sign_in: "Inloggen",
    full_name: "Volledige naam",
    email: "E-mailadres",
    phone: "Telefoonnummer",
    location: "Locatie",
    password: "Wachtwoord",
    confirm_password: "Bevestig wachtwoord",
    start_selling: "Begin met verkopen",
    creating_account: "Account aanmaken...",
    or_continue_with: "Of ga verder met",
    private_account: "Privé",
    business_account: "Zakelijk",
    business_name: "Bedrijfsnaam",
    kvk_number: "KVK-nummer",
    kvk_document: "KVK Document",
    id_document: "ID Document Eigenaar",
    upload_document: "Upload Document",
    document_uploaded: "Document geüpload",
  },
  es: {
    hero_title: "¿Listo para vender algo?",
    hero_subtitle: "Únete a miles de vendedores en el mercado de más rápido crecimiento del Caribe",
    benefit_1: "Anuncios gratis",
    benefit_1_desc: "Publica tus artículos sin cargos",
    benefit_2: "Alcanza compradores locales",
    benefit_2_desc: "Conéctate con compradores en tu área",
    benefit_3: "Rápido y fácil",
    benefit_3_desc: "Comienza a vender en menos de 2 minutos",
    create_account: "Crea tu cuenta de vendedor",
    already_have_account: "¿Ya tienes una cuenta?",
    sign_in: "Iniciar sesión",
    full_name: "Nombre completo",
    email: "Correo electrónico",
    phone: "Número de teléfono",
    location: "Ubicación",
    password: "Contraseña",
    confirm_password: "Confirmar contraseña",
    start_selling: "Comenzar a vender",
    creating_account: "Creando cuenta...",
    or_continue_with: "O continuar con",
    private_account: "Privado",
    business_account: "Empresa",
    business_name: "Nombre de empresa",
    kvk_number: "Número de registro comercial",
    kvk_document: "Documento de registro",
    id_document: "Documento de identidad",
    upload_document: "Subir documento",
    document_uploaded: "Documento subido",
  },
  pap: {
    hero_title: "Kier bende algu?",
    hero_subtitle: "Hunte ku miles di bendedónan riba e marketplace mas rápido kresiendo di Karibe",
    benefit_1: "Anuncionan grátis",
    benefit_1_desc: "Pone bo artíkulonan sin kosto",
    benefit_2: "Alkansá kumpradónan lokal",
    benefit_2_desc: "Konektá ku kumpradónan den bo área",
    benefit_3: "Rápido i fásil",
    benefit_3_desc: "Kuminsá bende den ménos ku 2 minüt",
    create_account: "Krea bo kuenta di bendedó",
    already_have_account: "Tin un kuenta kaba?",
    sign_in: "Inisiá sesión",
    full_name: "Nòmber kompletu",
    email: "Adrès di e-mail",
    phone: "Número di telefòn",
    location: "Lokashon",
    password: "Pasobra",
    confirm_password: "Konfirmá pasobra",
    start_selling: "Kuminsá bende",
    creating_account: "Ta krea kuenta...",
    or_continue_with: "Of sigui ku",
    private_account: "Privá",
    business_account: "Negoshi",
    business_name: "Nòmber di negoshi",
    kvk_number: "Número di KVK",
    kvk_document: "Dokumento KVK",
    id_document: "Dokumento di identidat",
    upload_document: "Subi dokumento",
    document_uploaded: "Dokumento subí",
  }
};

// Google Icon Component
const GoogleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const StartSellingPage = () => {
  const navigate = useNavigate();
  const { register, loginWithToken, user } = useAuth();
  const { language } = useLanguage();
  
  const t = (key) => SELLING_TRANSLATIONS[language]?.[key] || SELLING_TRANSLATIONS.en[key] || key;
  
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [socialConfig, setSocialConfig] = useState({ google_enabled: true });
  const [islands, setIslands] = useState([]);
  const [accountType, setAccountType] = useState('personal'); // 'personal' or 'business'
  const [uploadingDocs, setUploadingDocs] = useState(false);
  const kvkInputRef = useRef(null);
  const idInputRef = useRef(null);
  
  // Store files locally (not URLs) - will upload after registration
  const [kvkDocument, setKvkDocument] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    location: '',
    preferred_language: language,
    preferred_currency: 'USD',
    // Business fields
    business_name: '',
    kvk_number: '',
  });

  // If already logged in, redirect to create listing
  useEffect(() => {
    if (user) {
      navigate('/create-listing', { replace: true });
    }
  }, [user, navigate]);

  // Fetch active islands for location dropdown
  useEffect(() => {
    const fetchIslands = async () => {
      try {
        const res = await axios.get(`${API}/islands`);
        setIslands(res.data || []);
      } catch (error) {
        console.error('Failed to fetch islands');
      }
    };
    fetchIslands();
  }, []);

  // Fetch social config
  useEffect(() => {
    const fetchSocialConfig = async () => {
      try {
        const res = await axios.get(`${API}/auth/social-login-config`);
        setSocialConfig(res.data);
      } catch (error) {
        console.log('Using default social config');
      }
    };
    fetchSocialConfig();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLocationChange = (value) => {
    setFormData({ ...formData, location: value });
  };

  // Handle document selection (store locally, upload after registration)
  const handleDocumentSelect = (file, type) => {
    if (!file) return;
    
    if (type === 'kvk') {
      setKvkDocument(file);
      toast.success(t('document_uploaded'));
    } else {
      setIdDocument(file);
      toast.success(t('document_uploaded'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    // Validate business fields
    if (accountType === 'business') {
      if (!formData.business_name.trim()) {
        toast.error('Business name is required');
        return;
      }
      if (!formData.kvk_number.trim()) {
        toast.error('KVK number is required');
        return;
      }
    }
    
    setLoading(true);
    try {
      const registrationData = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        phone: formData.phone,
        location: formData.location,
        preferred_language: formData.preferred_language,
        preferred_currency: formData.preferred_currency,
        account_type: accountType,
      };
      
      // Add business fields if business account
      if (accountType === 'business') {
        registrationData.business_name = formData.business_name;
        registrationData.kvk_number = formData.kvk_number;
      }
      
      const success = await register(registrationData);
      
      if (success) {
        // Upload business documents AFTER registration (user is now authenticated)
        if (accountType === 'business' && (kvkDocument || idDocument)) {
          setUploadingDocs(true);
          try {
            const docFormData = new FormData();
            if (kvkDocument) {
              docFormData.append('kvk_document', kvkDocument);
            }
            if (idDocument) {
              docFormData.append('id_document', idDocument);
            }
            docFormData.append('id_type', 'id_card'); // Default type
            
            await axios.post(`${API}/upload/business-documents`, docFormData, {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            });
          } catch (docError) {
            console.error('Document upload failed:', docError);
            toast.error('Account created but document upload failed. Please upload documents later from your profile.');
          } finally {
            setUploadingDocs(false);
          }
        }
        
        toast.success('Account created! Let\'s create your first listing.');
        // Redirect to create listing page
        navigate('/create-listing', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    setGoogleLoading(true);
    // Store redirect destination for after login
    localStorage.setItem('postAuthRedirect', '/create-listing');
    // Use Emergent Auth for Google login (same as AuthPage)
    const redirectUrl = window.location.origin + '/auth';
    const appName = encodeURIComponent('Your Buy Site');
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&app_name=${appName}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <img src={HEADER_LOGO_DARK} alt="Logo" className="h-8" />
          </a>
          <a 
            href="/auth" 
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {t('already_have_account')} <span className="text-primary font-medium">{t('sign_in')}</span>
          </a>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8 lg:py-16">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          
          {/* Left Column - Hero & Benefits */}
          <div className="lg:sticky lg:top-32">
            {/* Hero Text */}
            <div className="mb-10">
              <h1 className="text-4xl lg:text-5xl font-bold text-foreground mb-4 leading-tight">
                {t('hero_title')}
              </h1>
              <p className="text-lg text-muted-foreground">
                {t('hero_subtitle')}
              </p>
            </div>
            
            {/* Benefits */}
            <div className="space-y-6">
              {/* Benefit 1 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-colors">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <Tag className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('benefit_1')}</h3>
                  <p className="text-sm text-muted-foreground">{t('benefit_1_desc')}</p>
                </div>
              </div>
              
              {/* Benefit 2 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-colors">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('benefit_2')}</h3>
                  <p className="text-sm text-muted-foreground">{t('benefit_2_desc')}</p>
                </div>
              </div>
              
              {/* Benefit 3 */}
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-card border border-border/50 hover:border-primary/30 transition-colors">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{t('benefit_3')}</h3>
                  <p className="text-sm text-muted-foreground">{t('benefit_3_desc')}</p>
                </div>
              </div>
            </div>
            
            {/* Trust Indicators */}
            <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Cancel anytime</span>
              </div>
            </div>
          </div>
          
          {/* Right Column - Sign Up Form */}
          <div className="bg-card border border-border rounded-3xl p-6 lg:p-8 shadow-xl">
            <h2 className="text-xl font-semibold text-foreground mb-6">{t('create_account')}</h2>
            
            {/* Account Type Toggle */}
            <div className="mb-6">
              <div className="flex bg-secondary rounded-xl p-1">
                <button
                  type="button"
                  onClick={() => setAccountType('personal')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    accountType === 'personal'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="account-type-personal"
                >
                  <User className="w-4 h-4" />
                  {t('private_account')}
                </button>
                <button
                  type="button"
                  onClick={() => setAccountType('business')}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                    accountType === 'business'
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="account-type-business"
                >
                  <Building2 className="w-4 h-4" />
                  {t('business_account')}
                </button>
              </div>
            </div>
            
            {/* Google Sign Up */}
            {socialConfig.google_enabled && (
              <>
                <button
                  onClick={handleGoogleSignIn}
                  disabled={googleLoading}
                  className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-white dark:bg-secondary border border-border rounded-xl font-medium text-foreground hover:bg-gray-50 dark:hover:bg-secondary/80 transition-colors disabled:opacity-50"
                  data-testid="google-signup-btn"
                >
                  {googleLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <GoogleIcon className="w-5 h-5" />
                      Continue with Google
                    </>
                  )}
                </button>
                
                <div className="relative my-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="px-3 bg-card text-muted-foreground">{t('or_continue_with')}</span>
                  </div>
                </div>
              </>
            )}
            
            {/* Sign Up Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Business Fields - Only show for business accounts */}
              {accountType === 'business' && (
                <>
                  {/* Business Name */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{t('business_name')} *</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="text"
                        name="business_name"
                        value={formData.business_name}
                        onChange={handleChange}
                        placeholder="Your Business Name"
                        className="w-full py-3 pl-11 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                        required={accountType === 'business'}
                        data-testid="business-name-input"
                      />
                    </div>
                  </div>
                  
                  {/* KVK Number */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">{t('kvk_number')} *</label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <input
                        type="text"
                        name="kvk_number"
                        value={formData.kvk_number}
                        onChange={handleChange}
                        placeholder="12345678"
                        className="w-full py-3 pl-11 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                        required={accountType === 'business'}
                        data-testid="kvk-number-input"
                      />
                    </div>
                  </div>
                  
                  {/* Document Uploads */}
                  <div className="grid grid-cols-2 gap-3">
                    {/* KVK Document */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{t('kvk_document')}</label>
                      <input
                        type="file"
                        ref={kvkInputRef}
                        accept="image/*,.pdf"
                        onChange={(e) => handleDocumentSelect(e.target.files[0], 'kvk')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => kvkInputRef.current?.click()}
                        className={`w-full py-3 px-4 border-2 border-dashed rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${
                          kvkDocument 
                            ? 'border-green-500 bg-green-500/10 text-green-600' 
                            : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {kvkDocument ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span className="truncate max-w-[80px]">{kvkDocument.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                    
                    {/* ID Document */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1.5">{t('id_document')}</label>
                      <input
                        type="file"
                        ref={idInputRef}
                        accept="image/*,.pdf"
                        onChange={(e) => handleDocumentSelect(e.target.files[0], 'id')}
                        className="hidden"
                      />
                      <button
                        type="button"
                        onClick={() => idInputRef.current?.click()}
                        className={`w-full py-3 px-4 border-2 border-dashed rounded-xl text-sm transition-all flex items-center justify-center gap-2 ${
                          idDocument 
                            ? 'border-green-500 bg-green-500/10 text-green-600' 
                            : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        {idDocument ? (
                          <>
                            <CheckCircle className="w-4 h-4" />
                            <span className="truncate max-w-[80px]">{idDocument.name}</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
              
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('full_name')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Doe"
                    className="w-full py-3 pl-11 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>
              
              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full py-3 pl-11 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>
              
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('phone')}</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+599 9 XXX XXXX"
                    className="w-full py-3 pl-11 pr-4 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                    data-testid="phone-input"
                  />
                </div>
              </div>
              
              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('location')} <span className="text-muted-foreground font-normal">(Optional)</span></label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
                  <SafariDropdown
                    value={formData.location}
                    onChange={handleLocationChange}
                    options={[
                      { value: '', label: 'Select your island...' },
                      ...islands.map(island => ({
                        value: island.name,
                        label: `${island.flag || '🏝️'} ${island.name}`
                      })),
                      { value: 'Other', label: '🌍 Other' }
                    ]}
                    placeholder="Select your island..."
                    className="w-full [&_button]:pl-11"
                    testId="location-select"
                  />
                </div>
              </div>
              
              {/* Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                    className="w-full py-3 pl-11 pr-12 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                    required
                    minLength={8}
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{t('confirm_password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm your password"
                    className="w-full py-3 pl-11 pr-12 bg-secondary border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-all"
                    required
                    data-testid="confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading || uploadingDocs}
                className="w-full py-3.5 mt-2 bg-primary text-white font-semibold rounded-xl hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-btn"
              >
                {loading || uploadingDocs ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {uploadingDocs ? 'Uploading documents...' : t('creating_account')}
                  </>
                ) : (
                  <>
                    {t('start_selling')}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
            
            {/* Sign In Link */}
            <p className="mt-6 text-center text-sm text-muted-foreground">
              {t('already_have_account')}{' '}
              <a href="/auth" className="text-primary font-medium hover:underline">
                {t('sign_in')}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartSellingPage;
